import { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, useParams } from 'react-router-dom';
import AutoHome from '@/auth/AutoHome';
import RequireAuth from '@/auth/RequireAuth';
import ProtectedRoute from '@/auth/ProtectedRoute';
import RequireAuthByRole from '@/routes/RequireAuthByRole';
import ConsultaInventoryOnly from '@/auth/ConsultaInventoryOnly';
import RequireConsultaIdePrices from '@/auth/RequireConsultaIdePrices';
import { AppErrorBoundary } from '@/components/AppErrorBoundary';

import RequireAdmin from '@/auth/RequireAdmin';
import { RequireRhRole, RhAprobacionesRedirect, RhAsistenciaRedirect, RhSolicitudesRedirect } from '@/routes/RequireRhRole';
import { RequireRhDocumentRole, RhDocumentsRedirect } from '@/routes/RequireRhDocumentRole';
import RequireAgendaEquipo from '@/auth/RequireAgendaEquipo';
import RequireGabineto from '@/auth/RequireGabineto';

const Shell = lazy(() => import('./layout/Shell'));
const VendedorLayout = lazy(() => import('./layout/VendedorLayout'));

async function loadMapModule<T>(loader: () => Promise<T>): Promise<T> {
  const [module, { configureLeafletDefaults }] = await Promise.all([
    loader(),
    import('./lib/leafletConfig'),
  ]);
  configureLeafletDefaults();
  return module;
}

const LoginPage = lazy(() => import('./pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const ActivateClientAccountPage = lazy(() => import('./pages/ActivateClientAccountPage'));
const Entradas = lazy(() => import('./pages/Entradas'));
const SalidasWizard = lazy(() => import('./pages/Salidas/Wizard'));
const Transferencia = lazy(() => import('./pages/Transferencia'));
const Conteo = lazy(() => import('./pages/Conteo'));
const ConteoCiclico = lazy(() => import('./pages/ConteoCiclico'));
const Actividades = lazy(() => import('./pages/Actividades'));
const ProyectoDetailPage = lazy(() => import('@/features/actividades/proyectos/ProyectoDetailPage'));
const Inventario = lazy(() => import('./pages/Inventario'));
const ConsultaIdePricesPage = lazy(() => import('./pages/ConsultaIdePricesPage'));
const Operaciones = lazy(() => import('./pages/Operaciones'));
const Admin = lazy(() => import('./pages/Admin'));
const AdminEtiquetas = lazy(() =>
  import('./features/labels/AdminEtiquetas').then((module) => ({ default: module.AdminEtiquetas })),
);
const AdminReports = lazy(() =>
  import('./features/reports/AdminReports').then((module) => ({ default: module.AdminReports })),
);
const AdminERP = lazy(() => import('./pages/AdminERP'));
const AgenteChat = lazy(() => import('./pages/AgenteChat'));
const TareaDetallePage = lazy(() => import('./pages/TareaDetallePage'));
const InventoryAccuracyDetailPage = lazy(() =>
  import('./pages/admin/InventoryAccuracyDetail').then((module) => ({
    default: module.InventoryAccuracyDetailPage,
  })),
);
const MovimientosPage = lazy(() => import('./pages/admin/MovimientosPage'));
const ValorInventarioPage = lazy(() => import('./pages/admin/ValorInventarioPage'));
const ReporteCotizaciones = lazy(() => import('./pages/ReporteCotizaciones'));
const AdminHome = lazy(() => import('./pages/admin/AdminHome'));
const AdminInventarioPage = lazy(() => import('./pages/admin/AdminInventarioPage'));
const InventoryComparisonPage = lazy(() => import('./pages/InventoryComparisonPage'));
const AdminRhPage = lazy(() => import('./pages/admin/AdminRhPage'));
const AdminDotsPage = lazy(() => import('./pages/admin/AdminDotsPage'));
const InventoryBulkImportPage = lazy(() => import('./pages/admin/InventoryBulkImportPage'));
const AdminVentasPage = lazy(() => import('./pages/admin/AdminVentasPage'));
const AdminVentasInformesPage = lazy(() => import('./pages/admin/AdminVentasPage').then((module) => ({ default: module.AdminVentasInformesPage })));
const AdminSales360SellerPage = lazy(() => import('./pages/admin/AdminSales360SellerPage'));
const AdminSales360ClientPage = lazy(() => import('./pages/admin/AdminSales360ClientPage'));
const AdminSales360SaiConfigPage = lazy(() => import('./pages/admin/AdminSales360SaiConfigPage'));
const AdminSalesPresentationsPage = lazy(() => import('./pages/admin/AdminSalesPresentationsPage'));
const AdminSalesContentPage = lazy(() => import('./pages/admin/AdminSalesContentPage'));
const UsersListPage = lazy(() => import('./pages/admin/UsersListPage'));
const UserCreatePage = lazy(() => import('./pages/admin/UserCreatePage'));
const UserDetailPage = lazy(() => import('./pages/admin/UserDetailPage'));
const LocationLabelsPage = lazy(() => import('./pages/admin/LocationLabelsPage'));
const OportunidadesReportePage = lazy(() => import('./pages/admin/OportunidadesReportePage'));
const AdminClientesMapaPage = lazy(() =>
  loadMapModule(() => import('./pages/admin/AdminClientesMapaPage')),
);
const RendimientoVendedoresPage = lazy(() => import('./pages/admin/RendimientoVendedoresPage'));
const MetasVendedorPage = lazy(() => import('./pages/admin/MetasVendedorPage'));
const CrmRecordatoriosConfigPage = lazy(() => import('./pages/admin/CrmRecordatoriosConfigPage'));
const AdminProductosPage = lazy(() => import('./pages/admin/AdminProductosPage'));
const AdminAgendaEquipoPage = lazy(() => import('./pages/admin/AdminAgendaEquipoPage'));
const AdminClientePortalRequestsPage = lazy(() => import('./pages/admin/AdminClientePortalRequestsPage'));
const AdminClientePortalHomePage = lazy(() => import('./pages/admin/AdminClientePortalHomePage'));
const AdminClientePortalInvoiceRequestsPage = lazy(
  () => import('./pages/admin/AdminClientePortalInvoiceRequestsPage'),
);
const AdminClientePortalOrderRequestsPage = lazy(
  () => import('./pages/admin/AdminClientePortalOrderRequestsPage'),
);
const AdminClientePortalQuoteIntakePage = lazy(
  () => import('./pages/admin/AdminClientePortalQuoteIntakePage'),
);
const AdminClientePortalQuoteReviewPage = lazy(() => import('./pages/admin/AdminClientePortalQuoteReviewPage'));
const AdminClientePortalBalancesPage = lazy(() => import('./pages/admin/AdminClientePortalBalancesPage'));
const AdminClientePortalCatalogActivityPage = lazy(
  () => import('./pages/admin/AdminClientePortalCatalogActivityPage'),
);
const AdminClientePortalAccessPage = lazy(() => import('./pages/admin/AdminClientePortalAccessPage'));
const ProveedorAgendaPage = lazy(() => import('./pages/proveedor/ProveedorAgendaPage'));
const ClientePortalPage = lazy(() => import('./pages/cliente/ClientePortalPage'));
const RhHomePage = lazy(() => import('./pages/rh/RhHomePage'));
const RhDocumentsPage = lazy(() => import('./pages/rh/RhDocumentsPage'));
const RhSolicitudesPage = lazy(() => import('./pages/rh/RhSolicitudesPage'));
const RhFeedbackPage = lazy(() => import('./pages/rh/RhFeedbackPage'));
const RhAprobacionesPage = lazy(() => import('./pages/rh/RhAprobacionesPage'));
const RhVacationBalancesPage = lazy(() => import('./pages/rh/RhVacationBalancesPage'));
const RhCalendarPage = lazy(() => import('./pages/rh/RhCalendarPage'));
const RhExpedientePage = lazy(() => import('./pages/rh/RhExpedientePage'));
const SupportHubPage = lazy(() => import('./pages/support/SupportHubPage'));
const BrandSupportPage = lazy(() => import('./pages/brandWork/BrandSupportPage'));
const SupportTicketsPage = lazy(() => import('./pages/support/SupportTicketsPage'));
const SupportSaiPage = lazy(() => import('./pages/support/SupportSaiPage'));
const Tracking = lazy(() => loadMapModule(() => import('./pages/Tracking')));
const RutasPage = lazy(() => loadMapModule(() => import('./pages/routes/RutasPage')));
const RutasPlannerPage = lazy(() =>
  loadMapModule(() => import('./pages/routes/RutasPage')).then((module) => ({
    default: module.RutasPlannerPage,
  })),
);
const RutasTowerPage = lazy(() =>
  loadMapModule(() => import('./pages/routes/RutasPage')).then((module) => ({
    default: module.RutasTowerPage,
  })),
);
const MyRoutesPage = lazy(() => loadMapModule(() => import('./pages/routes/MyRoutesPage')));
const AttendancePage = lazy(() => loadMapModule(() => import('./pages/AttendancePage')));
const EmployeeHomePage = lazy(() => import('./pages/home/EmployeeHomePage'));
const DailyPage = lazy(() => import('./pages/daily/DailyPage'));
const ReceivedFeedbackPage = lazy(() => import('./pages/feedback/ReceivedFeedbackPage'));
const InsightsPage = lazy(() => import('./pages/insights/InsightsPage'));
const AdminRhDocumentsPage = lazy(() => import('./pages/admin/AdminRhDocumentsPage'));
const AdminRhDocumentDetailPage = lazy(() => import('./pages/admin/AdminRhDocumentDetailPage'));
const AdminRhFeedbackPage = lazy(() => import('./pages/admin/AdminRhFeedbackPage'));
const AdminRhOfficeSupplyPage = lazy(() => import('./pages/admin/AdminRhOfficeSupplyPage'));
const AdminRhExpensesPage = lazy(() => import('./pages/admin/AdminRhExpensesPage'));
const AdminSupportTicketsPage = lazy(() => import('./pages/admin/AdminSupportTicketsPage'));
const DemoComprasLayout = lazy(() => import('./pages/demo/DemoComprasLayout'));
const DemoComprasPedidosPage = lazy(() => import('./pages/demo/DemoComprasPedidosPage'));
const DemoComprasSeguimientoPage = lazy(() => import('./pages/demo/DemoComprasSeguimientoPage'));
const DemoComprasReportesPage = lazy(() => import('./pages/demo/DemoComprasReportesPage'));
const VendedorHomePage = lazy(() => import('./pages/vendedor/VendedorHomePage'));
const VendedorClientesPage = lazy(() => import('./pages/vendedor/VendedorClientesPage'));
const VendedorCliente360Page = lazy(() => import('./pages/vendedor/VendedorCliente360Page'));
const VendedorAgendaPage = lazy(() => import('./pages/vendedor/VendedorAgendaPage'));
const VendedorActividadesPage = lazy(() => import('./pages/vendedor/VendedorActividadesPage'));
const VendedorMapaPage = lazy(() => loadMapModule(() => import('./pages/vendedor/VendedorMapaPage')));
const VendedorOportunidadesPage = lazy(() => import('./pages/vendedor/VendedorOportunidadesPage'));
const VendedorOportunidadDetallePage = lazy(() => import('./pages/vendedor/VendedorOportunidadDetallePage'));
const VendedorContenidoComercialPage = lazy(() => import('./pages/vendedor/sales-enablement/VendedorContenidoComercialPage'));
const SalesPresentationSessionPage = lazy(() => import('./pages/vendedor/sales-enablement/SalesPresentationSessionPage'));
const VendedorMileagePage = lazy(() => import('./pages/vendedor/VendedorMileagePage'));
const AdminMileagePage = lazy(() => import('./pages/admin/AdminMileagePage'));

function VendedorClientesRedirect() {
  const params = useParams();
  const id = params.id;
  return <Navigate to={id ? `/vendedor/prospectos/${encodeURIComponent(id)}` : '/vendedor/prospectos'} replace />;
}

const router = createBrowserRouter([
  { path: '/', element: <AutoHome /> },
  { path: '/login', element: <LoginPage /> },
  { path: '/olvide-mi-contrasena', element: <ForgotPasswordPage /> },
  { path: '/restablecer-contrasena', element: <ResetPasswordPage /> },
  { path: '/activar-cuenta', element: <ActivateClientAccountPage /> },
  { path: '/loginpage', element: <Navigate to="/login" replace /> },
  {
    path: '/vendedor',
    element: <RequireAuthByRole role="VENDEDOR" />,
    children: [
      { path: 'presentaciones/sesion/:sessionId', element: <SalesPresentationSessionPage /> },
      {
        element: <VendedorLayout />,
        children: [
          { index: true, element: <VendedorHomePage /> },
          { path: 'prospectos', element: <VendedorClientesPage mode="prospectos" /> },
          { path: 'prospectos/:id', element: <VendedorCliente360Page /> },
          { path: 'clientes', element: <VendedorClientesPage mode="clientes" /> },
          { path: 'clientes/:id', element: <VendedorClientesRedirect /> },
          { path: 'agenda', element: <VendedorAgendaPage /> },
          { path: 'actividades', element: <VendedorActividadesPage /> },
          { path: 'oportunidades', element: <VendedorOportunidadesPage /> },
          { path: 'oportunidades/:id', element: <VendedorOportunidadDetallePage /> },
          { path: 'contenido', element: <VendedorContenidoComercialPage /> },
          { path: 'mapa', element: <VendedorMapaPage /> },
          { path: 'kilometraje', element: <VendedorMileagePage /> },
        ],
      },
    ],
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <ConsultaInventoryOnly>
          <Shell />
        </ConsultaInventoryOnly>
      </RequireAuth>
    ),
    children: [
      {
        path: 'home',
        element: <EmployeeHomePage />,
      },
      {
        path: 'daily',
        element: <DailyPage />,
      },
      {
        path: 'feedback-recibido',
        element: <ReceivedFeedbackPage />,
      },
      {
        path: 'insights',
        element: <InsightsPage />,
      },
      {
        path: 'dashboard',
        element: (
          <ProtectedRoute requiredCapability="canViewDashboard">
            <Admin />
          </ProtectedRoute>
        ),
      },
      {
        path: 'inventario/comparar',
        element: (
          <ProtectedRoute requiredCapability="canViewInventory">
            <InventoryComparisonPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'inventario',
        element: (
          <ProtectedRoute requiredCapability="canViewInventory">
            <Inventario />
          </ProtectedRoute>
        ),
      },
      {
        path: 'precios',
        element: (
          <RequireConsultaIdePrices>
            <ConsultaIdePricesPage />
          </RequireConsultaIdePrices>
        ),
      },
      {
        path: 'proveedor/agenda',
        element: (
          <ProtectedRoute requiredCapability="canViewProviderAgenda">
            <ProveedorAgendaPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'cliente',
        element: (
          <ProtectedRoute requiredCapability="canViewClientPortal">
            <ClientePortalPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'operaciones',
        element: (
          <ProtectedRoute requiredCapability="canUseOperations">
            <Operaciones />
          </ProtectedRoute>
        ),
      },
      {
        path: 'entradas',
        element: (
          <ProtectedRoute requiredCapability="canUseOperations">
            <Entradas />
          </ProtectedRoute>
        ),
      },
      {
        path: 'salida',
        element: (
          <ProtectedRoute requiredCapability="canUseOperations">
            <Navigate to="/salidas" replace />
          </ProtectedRoute>
        ),
      },
      {
        path: 'salidas',
        element: (
          <ProtectedRoute requiredCapability="canUseOperations">
            <SalidasWizard />
          </ProtectedRoute>
        ),
      },
      {
        path: 'transferencia',
        element: (
          <ProtectedRoute requiredCapability="canUseOperations">
            <Transferencia />
          </ProtectedRoute>
        ),
      },
      {
        path: 'conteo-ciclico',
        element: (
          <ProtectedRoute requiredCapability="canUseOperations">
            <ConteoCiclico />
          </ProtectedRoute>
        ),
      },
      {
        path: 'conteo',
        element: (
          <ProtectedRoute requiredCapability="canUseOperations">
            <Conteo />
          </ProtectedRoute>
        ),
      },
      {
        path: 'actividades',
        element: (
          <ProtectedRoute requiredCapability="canViewActivities">
            <Actividades />
          </ProtectedRoute>
        ),
      },
      {
        path: 'actividades/pendientes',
        element: (
          <ProtectedRoute requiredCapability="canViewActivities">
            <Navigate to="/actividades?tab=mis-tareas" replace />
          </ProtectedRoute>
        ),
      },
      {
        path: 'actividades/alertas',
        element: (
          <ProtectedRoute requiredCapability="canViewActivities">
            <Navigate to="/actividades?tab=mis-tareas" replace />
          </ProtectedRoute>
        ),
      },
      {
        path: 'actividades/proyectos',
        element: (
          <ProtectedRoute requiredCapability="canViewActivities">
            <Navigate to="/actividades?tab=proyectos" replace />
          </ProtectedRoute>
        ),
      },
      {
        path: 'actividades/proyectos/:proyectoId',
        element: (
          <ProtectedRoute requiredCapability="canViewActivities">
            <ProyectoDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'actividades/tareas/:taskId',
        element: (
          <ProtectedRoute requiredCapability="canViewActivities">
            <TareaDetallePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tracking',
        element: (
          <ProtectedRoute requiredCapability="canViewDashboard">
            <Tracking />
          </ProtectedRoute>
        ),
      },
      {
        path: 'rutas',
        element: (
          <ProtectedRoute requiredCapability="canUseRoutes">
            <RutasPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'rutas/torre',
        element: (
          <ProtectedRoute requiredCapability="canUseRoutes">
            <RutasTowerPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'rutas/planificar',
        element: (
          <ProtectedRoute requiredCapability="canUseRoutes">
            <RutasPlannerPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'rutas/mis-rutas',
        element: (
          <ProtectedRoute requiredCapability="canUseRoutes">
            <MyRoutesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'democompras',
        element: (
          <ProtectedRoute requiredCapability="canViewAdminInventory">
            <DemoComprasLayout />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <Navigate to="/democompras/reportes" replace /> },
          { path: 'pedidos', element: <DemoComprasPedidosPage /> },
          { path: 'seguimiento', element: <DemoComprasSeguimientoPage /> },
          { path: 'reportes', element: <DemoComprasReportesPage /> },
        ],
      },
      {
        path: 'asistencia',
        element: <AttendancePage />,
      },
      {
        path: 'kilometraje',
        element: <VendedorMileagePage />,
      },
      {
        path: 'documentos-por-firmar',
        element: (
          <RequireRhDocumentRole mode="employee">
            <RhDocumentsPage />
          </RequireRhDocumentRole>
        ),
      },
      {
        path: 'solicitudes',
        element: (
          <RequireRhRole mode="employee">
            <RhSolicitudesPage />
          </RequireRhRole>
        ),
      },
      {
        path: 'calendario',
        element: <RhCalendarPage />,
      },
      {
        path: 'solicitudes/nueva',
        element: (
          <RequireRhRole mode="employee">
            <RhSolicitudesPage />
          </RequireRhRole>
        ),
      },
      {
        path: 'solicitudes/mi-saldo',
        element: (
          <RequireRhRole mode="employee">
            <RhSolicitudesPage />
          </RequireRhRole>
        ),
      },
      {
        path: 'quejas-sugerencias',
        element: <RhFeedbackPage />,
      },
      {
        path: 'apoyo-marcas',
        element: <BrandSupportPage />,
      },
      {
        path: 'soporte',
        element: <SupportHubPage />,
      },
      {
        path: 'soporte/tickets',
        element: <Navigate to="/soporte" replace />,
      },
      {
        path: 'soporte/kora',
        element: <SupportTicketsPage product="KORA" />,
      },
      {
        path: 'soporte/sai',
        element: <SupportSaiPage />,
      },
      {
        path: 'rh',
        element: <RhHomePage />,
      },
      {
        path: 'rh/asistencia',
        element: <RhAsistenciaRedirect />,
      },
      {
        path: 'rh/solicitudes',
        element: <RhSolicitudesRedirect />,
      },
      {
        path: 'rh/documentos',
        element: <RhDocumentsRedirect />,
      },
      {
        path: 'rh/aprobaciones',
        element: <RhAprobacionesRedirect />,
      },
      {
        path: 'rh/solicitudes/aprobaciones',
        element: <RhAprobacionesRedirect />,
      },
      {
        path: 'agente',
        element: (
          <RequireGabineto>
            <ProtectedRoute requiredCapability="canUseAgent">
              <AgenteChat />
            </ProtectedRoute>
          </RequireGabineto>
        ),
      },
      {
        path: 'agenda-equipo',
        element: (
          <RequireAgendaEquipo>
            <AdminAgendaEquipoPage />
          </RequireAgendaEquipo>
        ),
      },
      {
        path: 'admin',
        element: <RequireAdmin />,
        children: [
          { index: true, element: <AdminHome /> },
          { path: 'rh', element: <AdminRhPage /> },
          { path: 'rh/expediente', element: <RhExpedientePage /> },
          { path: 'rh/dots', element: <AdminDotsPage /> },
          { path: 'rh/quejas-sugerencias', element: <AdminRhFeedbackPage /> },
          { path: 'rh/material-oficina', element: <AdminRhOfficeSupplyPage /> },
          { path: 'rh/gastos', element: <AdminRhExpensesPage /> },
          { path: 'rh/documentos', element: <AdminRhDocumentsPage /> },
          { path: 'rh/documentos/:documentId', element: <AdminRhDocumentDetailPage /> },
          { path: 'rh/aprobaciones', element: <RhAprobacionesPage /> },
          { path: 'rh/saldos-vacaciones', element: <RhVacationBalancesPage /> },
          { path: 'rh/solicitudes', element: <Navigate to="/admin/rh/aprobaciones" replace /> },
          { path: 'rh/asistencia', element: <AttendancePage /> },
          { path: 'soporte/tickets', element: <AdminSupportTicketsPage /> },
          { path: 'inventario', element: <AdminInventarioPage /> },
          { path: 'inventario/carga-masiva', element: <InventoryBulkImportPage /> },
          { path: 'ventas', element: <AdminVentasPage /> },
          { path: 'ventas/informes', element: <AdminVentasInformesPage /> },
          { path: 'ventas/vendedores/:id', element: <AdminSales360SellerPage /> },
          { path: 'ventas/clientes/:id', element: <AdminSales360ClientPage /> },
          { path: 'ventas/integracion-sai', element: <AdminSales360SaiConfigPage /> },
          { path: 'ventas/presentaciones', element: <AdminSalesPresentationsPage /> },
          { path: 'ventas/contenido', element: <AdminSalesContentPage /> },
          { path: 'ventas/kilometraje', element: <AdminMileagePage /> },
          { path: 'usuarios', element: <UsersListPage /> },
          { path: 'usuarios/nuevo', element: <UserCreatePage /> },
          { path: 'usuarios/:id', element: <UserDetailPage /> },
          { path: 'etiquetas/ubicaciones', element: <LocationLabelsPage /> },
          { path: 'etiquetas', element: <AdminEtiquetas /> },
          { path: 'reportes', element: <AdminReports /> },
          { path: 'reportes/oportunidades', element: <OportunidadesReportePage /> },
          { path: 'rendimiento-vendedores', element: <RendimientoVendedoresPage /> },
          { path: 'metas-vendedor', element: <MetasVendedorPage /> },
          { path: 'crm/recordatorios', element: <CrmRecordatoriosConfigPage /> },
          { path: 'productos', element: <AdminProductosPage /> },
          { path: 'agenda-equipo', element: <AdminAgendaEquipoPage /> },
          { path: 'erp', element: <AdminERP /> },
          { path: 'reportes/cotizaciones', element: <ReporteCotizaciones /> },
          { path: 'movimientos', element: <MovimientosPage /> },
          { path: 'inventario/exactitud', element: <InventoryAccuracyDetailPage /> },
          { path: 'inventario/valor', element: <ValorInventarioPage /> },
          { path: 'clientes/mapa', element: <AdminClientesMapaPage /> },
          { path: 'cliente-portal', element: <AdminClientePortalHomePage /> },
          { path: 'cliente-portal/solicitudes', element: <AdminClientePortalRequestsPage /> },
          { path: 'cliente-portal/quote-intake', element: <AdminClientePortalQuoteIntakePage /> },
          { path: 'cliente-portal/quote-intake/:requestId', element: <AdminClientePortalQuoteReviewPage /> },
          { path: 'cliente-portal/order-requests', element: <AdminClientePortalOrderRequestsPage /> },
          { path: 'cliente-portal/invoice-requests', element: <AdminClientePortalInvoiceRequestsPage /> },
          { path: 'cliente-portal/saldos', element: <AdminClientePortalBalancesPage /> },
          { path: 'cliente-portal/actividad-existencias', element: <AdminClientePortalCatalogActivityPage /> },
          { path: 'cliente-portal/accesos', element: <AdminClientePortalAccessPage /> },
        ],
      },
    ],
  },
]);

export default function App() {
  return (
    <AppErrorBoundary>
      <Suspense fallback={<RouteLoadingFallback />}>
        <RouterProvider router={router} />
      </Suspense>
    </AppErrorBoundary>
  );
}

function RouteLoadingFallback() {
  return (
    <main className="kora-route-loading" role="status" aria-live="polite">
      <span className="kora-route-loading__mark" aria-hidden="true" />
      <div>
        <strong>Abriendo KORA</strong>
        <span>Cargando tu espacio de trabajo...</span>
      </div>
    </main>
  );
}
